import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLatest } from './useLatest';
import { useToast } from '../context';
import { useLang } from '../i18n';
import { localDateISO } from '../logic/dates';
import { boardState, splitOpenList, listDayOf } from '../logic/openList';
import { closeList, reopenList } from '../data/items';
import { useSaveFailed } from './useSaveFailed';

/**
 * The open list's gate and its "Move list to History" dialog — what OpenListCtx serves.
 *
 * Every ADD goes through `withList(run, label)`. It runs `run(listDay, { archived })` at
 * once — unless a new list cycle has begun (closing time) on a list nobody has added to
 * since; then the dialog asks first: "move the open list to History & start a new one"
 * (optionally keeping unfinished items) or "keep adding to the same list". `archived` =
 * ids that step moved to History, so a caller can tell whether an item it saw on the old
 * list is still open. `openMove()` opens the same dialog by hand (📦 Move list to History).
 *
 * @returns {{ ctx: { withList, openMove, board }, dialog: object|null }} — render
 *   <ListDialog {...dialog} /> when `dialog` is set.
 */
export function useOpenListGate(boardItems, author) {
  const notify = useToast();
  const failed = useSaveFailed();
  const { t } = useLang();
  const [dlg, setDlg] = useState(null);   // { mode: 'add'|'move', label?, run? }
  const decidedFor = useRef('');          // list-day this device chose "keep adding" for
  const itemsRef = useLatest(boardItems);

  // Time-dependent (8 PM starts a new cycle), so worked out every render — but handed
  // out as the same object until something in it changes, so consumers don't re-render.
  const st = boardState(boardItems);
  const boardKey = `${st.curDay}|${st.listDay}|${st.firstDay}|${st.stale}|${st.count}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `st` is captured whenever boardKey changes
  const board = useMemo(() => st, [boardKey]);

  const withList = useCallback((run, label) => {
    const now = boardState(itemsRef.current);
    if (!now.stale || decidedFor.current === now.curDay) { run(now.curDay, { archived: new Set() }); return; }
    setDlg({ mode: 'add', run, label });
  }, [itemsRef]);
  const openMove = useCallback(() => setDlg({ mode: 'move' }), []);
  const ctx = useMemo(() => ({ withList, openMove, board }), [withList, openMove, board]);

  // Move the open list to History, optionally keeping unfinished items on the new list,
  // then run the add that was waiting (if any). Undo re-opens exactly what moved.
  const onMove = ({ keepRecent, keepOlder }) => {
    const waiting = dlg;
    setDlg(null);
    const now = boardState(boardItems);
    const split = splitOpenList(boardItems, localDateISO());
    const keep = [...(keepRecent ? split.recent : []), ...(keepOlder ? split.older : [])];
    const keepIds = new Set(keep.map(i => i.id));
    const archiveIds = boardItems.filter(i => !keepIds.has(i.id)).map(i => i.id);
    const carry = keep.map(i => ({ id: i.id, fromDay: listDayOf(i) !== now.curDay ? listDayOf(i) : null }));
    const undo = { archiveIds, carry: keep.map(i => ({ id: i.id, listFor: i.listFor || null, carriedFrom: i.carriedFrom || null })) };
    closeList({ archiveIds, carry, toDay: now.curDay, by: author }).catch(failed);
    decidedFor.current = now.curDay;
    if (waiting && waiting.run) waiting.run(now.curDay, { archived: new Set(archiveIds) });
    // Shown after the waiting add so ITS toast doesn't replace this (bigger) undo.
    notify(keep.length ? t('list.movedKept', { n: archiveIds.length, k: keep.length }) : t('list.moved', { n: archiveIds.length }),
      () => reopenList(undo).catch(failed));
  };
  const onSame = () => {
    const waiting = dlg;
    setDlg(null);
    const now = boardState(boardItems);
    decidedFor.current = now.curDay;
    if (waiting && waiting.run) waiting.run(now.curDay, { archived: new Set() });
  };
  // Another device emptied the board while the dialog was open → nothing left to ask
  // about; just do the waiting add (it starts the new list).
  useEffect(() => {
    if (dlg && boardItems.length === 0) {
      const waiting = dlg;
      setDlg(null);
      if (waiting.run) waiting.run(boardState([]).curDay, { archived: new Set() });
    }
  }, [dlg, boardItems.length]);

  const dialog = dlg && boardItems.length > 0 ? {
    mode: dlg.mode, label: dlg.label, board,
    split: splitOpenList(boardItems, localDateISO()),
    onMove, onSame, onClose: () => setDlg(null),
  } : null;
  return { ctx, dialog };
}
